import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import MainLayout from "./MainLayout";
import {
  ChevronLeft, Download, Printer,
  FileDown, X, Calendar, Search, Loader2, Filter, RotateCcw,
  Pencil, Check, MapPin, AlertTriangle
} from "lucide-react";
import toast from "react-hot-toast";
import "../styles/dailyAttendanceReport.css";
import Footer from "./Footer";
import { useAuth } from "../context/AuthContext";
import { describeRecordLocation } from "../utils/geolocation";
import api from "../api";
import { Tooltip } from "antd";

const getLocationInfo = (dayData, which = "check_in") => {
  if (!dayData) return null;

  const recordToDescribe = {
    ...dayData,
    location_status: dayData.location_status || dayData.check_in_location_status || dayData.checkInLocationStatus,
    check_in_address: dayData.check_in_address || dayData.checkInAddress || dayData.checkInLocation || dayData.check_in_location || dayData.location,
    check_in_latitude: dayData.check_in_latitude ?? dayData.checkInLatitude ?? dayData.latitude,
    check_in_longitude: dayData.check_in_longitude ?? dayData.checkInLongitude ?? dayData.longitude,
    check_out_location_status: dayData.check_out_location_status || dayData.checkOutLocationStatus,
    check_out_address: dayData.check_out_address || dayData.checkOutAddress || dayData.checkOutLocation || dayData.check_out_location,
    check_out_latitude: dayData.check_out_latitude ?? dayData.checkOutLatitude,
    check_out_longitude: dayData.check_out_longitude ?? dayData.checkOutLongitude,
  };

  const locationObj = describeRecordLocation(recordToDescribe, which);
  if (locationObj && (locationObj.label || locationObj.detail)) {
    return locationObj;
  }

  if (which === "check_in") {
    const text = recordToDescribe.check_in_address;
    if (text && typeof text === 'string') return { label: text, detail: null };
  } else {
    const text = recordToDescribe.check_out_address;
    if (text && typeof text === 'string') return { label: text, detail: null };
  }

  return null;
};

export default function DailyAttendanceReport() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, logout } = useAuth();

  const initialStatusFilter = location.state?.filterStatus || "ALL";
  const initialDateFilter = location.state?.filterDate;
  const initialDeptFilter = location.state?.filterDept || "ALL";

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [attendanceData, setAttendanceData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState(initialDateFilter ? "custom" : "today");
  const [customDate, setCustomDate] = useState(initialDateFilter || new Date().toISOString().split('T')[0]);
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState(initialStatusFilter);
  const [deptFilter, setDeptFilter] = useState(initialDeptFilter);

  // Edit states
  const [editingRow, setEditingRow] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [saving, setSaving] = useState(false);

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);
  const toggleFilters = () => setShowFilters((prev) => !prev);

  // Fetch logic
  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let startDateStr, endDateStr;
      const now = new Date();
      endDateStr = now.toISOString().split('T')[0];

      if (filterType === "today") {
        startDateStr = endDateStr;
      } else if (filterType === "yesterday") {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        startDateStr = yesterday.toISOString().split('T')[0];
        endDateStr = startDateStr;
      } else if (filterType === "last7") {
        const last7 = new Date();
        last7.setDate(last7.getDate() - 7);
        startDateStr = last7.toISOString().split('T')[0];
      } else {
        startDateStr = customDate;
        endDateStr = customDate;
      }

      const start = new Date(startDateStr);
      const end = new Date(endDateStr);

      const monthsToFetch = new Set();
      // We will loop from a copy of start to end, because we need start to be intact for the next loop.
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        monthsToFetch.add(`${d.getFullYear()}-${d.getMonth() + 1}`);
      }

      const fetchPromises = Array.from(monthsToFetch).map(async (my) => {
        const [year, month] = my.split('-');
        try {
          const res = await api.get(`/api/attendance-report/report?month=${month}&year=${year}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          return res.data?.success ? (res.data.data?.employees || []) : [];
        } catch (e) {
          return [];
        }
      });

      // Also fetch master list to ensure absent employees without records are included
      let allMasterEmp = [];
      try {
        const empRes = await api.get(`/api/employee-master?page=1&limit=9999`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const empJson = empRes.data;
        allMasterEmp = empJson?.data?.employees || empJson?.employees || empJson?.rows || empJson?.data || [];
        if (!Array.isArray(allMasterEmp) && Array.isArray(empJson?.data?.data)) {
          allMasterEmp = empJson.data.data;
        }
      } catch (e) {}

      // Fetch departments to map IDs to names
      let deptCodeMap = {};
      try {
        const deptRes = await api.get("/api/department-master", {
          headers: { Authorization: `Bearer ${token}` }
        });
        const rawDepts = deptRes.data?.data || deptRes.data?.departments || deptRes.data || [];
        if (Array.isArray(rawDepts)) {
          rawDepts.forEach(d => {
            const code = d.department_id || d.id || d.dept_code || d.code;
            const name = d.department_name || d.name || d.dept_name;
            if (code && name) deptCodeMap[String(code).toUpperCase()] = name;
          });
        }
      } catch (e) {}

      const results = await Promise.all(fetchPromises);

      // Merge employee data from multiple months
      const employeeMap = new Map();
      
      // Seed with all master employees
      if (Array.isArray(allMasterEmp)) {
        allMasterEmp.forEach(emp => {
          const id = emp.empId || emp.employee_id || emp.employeeId || emp.id;
          if (id) {
            const deptIdStr = String(emp.department || emp.department_id || emp.dept || '').toUpperCase();
            const dName = deptCodeMap[deptIdStr] || emp.department_name || emp.departmentName || deptIdStr || 'Engineering';
            employeeMap.set(String(id).toUpperCase(), { 
              empId: id, 
              fullName: emp.name || emp.fullName || (emp.first_name ? emp.first_name + ' ' + (emp.last_name || '') : '') || 'Unknown', 
              department: dName,
              attendance: {} 
            });
          }
        });
      }

      results.forEach(empList => {
        if (!Array.isArray(empList)) return;
        empList.forEach(emp => {
          const empIdStr = String(emp.empId || emp.employeeId || '').toUpperCase();
          if (!empIdStr) return;
          
          if (!employeeMap.has(empIdStr)) {
            employeeMap.set(empIdStr, { ...emp, attendance: { ...emp.attendance } });
          } else {
            const existing = employeeMap.get(empIdStr);
            existing.attendance = { ...existing.attendance, ...emp.attendance };
            if (emp.fullName) existing.fullName = emp.fullName;
            if (emp.department) {
              const deptIdStr = String(emp.department || '').toUpperCase();
              existing.department = deptCodeMap[deptIdStr] || emp.department_name || emp.departmentName || deptIdStr;
            }
          }
        });
      });
      const allEmployees = Array.from(employeeMap.values());

      const allRecords = [];
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

      // Iterate through range again to build records
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const currentStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        const isFuture = currentStr > todayStr;
        const isWeekend = d.getDay() === 0 || d.getDay() === 6;

        allEmployees.forEach(emp => {
          const dayData = emp.attendance[currentStr];
          let checkInTime = null;
          let checkOutTime = null;
          let status;

          if (dayData) {
            checkInTime = dayData.checkIn || dayData.check_in || null;
            checkOutTime = dayData.checkOut || dayData.check_out || null;

            status = dayData.status;

            // Infer status if check-in exists but status is A or missing or weekend/holiday
            if (checkInTime || checkOutTime) {
              if (!status || status === "A" || status === "-" || status === "Weekend" || status === "WO" || status === "Holiday" || status === "H" || status === "W") {
                status = (checkInTime && !checkOutTime) ? "PC" : "P";
              }
            } else if (!status || status === "-" || status === "W" || status === "WO") {
              if ((dayData && dayData.holidayName) || status === "H" || status === "Holiday") {
                status = "Holiday";
              } else {
                status = (isWeekend || status === "W" || status === "WO") ? "Weekend" : "A";
              }
            }

            if (isFuture && status === "A") {
              status = "-";
            }
          } else {
            if (isFuture) {
              status = isWeekend ? "Weekend" : "-";
            } else {
              status = isWeekend ? "Weekend" : "A";
            }
          }

          allRecords.push({
            date: currentStr,
            empId: emp.empId,
            name: emp.fullName,
            checkIn: checkInTime,
            checkOut: checkOutTime,
            location: getLocationInfo(dayData, "check_in") || getLocationInfo(dayData, "check_out"),
            status: status || "-",
            holidayName: dayData ? (dayData.holidayName || null) : null,
            department: emp.department || emp.department_name || emp.departmentName || emp.dept || 'Engineering',
          });
        });
      }

      setAttendanceData(allRecords);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, customDate, filterType]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const filteredData = attendanceData.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      String(item.empId).toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchesSearch) return false;

    if (deptFilter !== "ALL") {
      const dName = String(item.department || "").toUpperCase();
      if (!dName.includes(deptFilter.toUpperCase())) return false;
    }

    const s = String(item.status || "").toUpperCase();

    if (statusFilter === "Present" || statusFilter === "P") {
      // Exclude Late if specifically filtering for Present
      if (s === "LT" || s === "LATE") return false;
      return s === "P" || s === "PC" || s === "PRESENT" || !!item.checkIn;
    }
    if (statusFilter === "Absent" || statusFilter === "A") {
      return s === "A" || s === "ABSENT";
    }
    if (statusFilter === "Late" || statusFilter === "LT") {
      return s === "LT" || s === "LATE";
    }
    if (statusFilter === "Leave" || statusFilter === "L" || statusFilter === "On Leave") {
      return s.includes("LEAVE") || s === "L" || s === "OD";
    }
    return true;
  });

  const formatDisplayTime = (isoString) => {
    if (!isoString) return "--:--";
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase();
  };

  const formatDateLabel = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d)) return '';
    const local = new Date(d.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    const day = String(local.getDate()).padStart(2, '0');
    const month = String(local.getMonth() + 1).padStart(2, '0');
    const year = local.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatLocationForCSV = (loc) => {
    if (!loc) return "--";
    if (typeof loc === "string") return `"${loc.replace(/"/g, '""')}"`;
    const parts = [];
    if (loc.label) parts.push(loc.label);
    if (loc.detail) parts.push(loc.detail);
    const text = parts.join(" - ") || "--";
    return `"${text.replace(/"/g, '""')}"`;
  };

  const renderLocationCell = (loc) => {
    if (!loc) return <span className="dr-no-location">--</span>;
    if (typeof loc === "string") return <span className="dr-location-text">{loc}</span>;

    return (
      <div className="dr-location-cell">
        {loc.label && (
          <span
            className={`dr-location-badge ${loc.tone ? `dr-badge-${loc.tone}` : ''}`}
            title={loc.title || loc.label}
          >
            <MapPin size={12} />
            {loc.label}
            {loc.lowConfidence && <AlertTriangle size={11} className="dr-location-warn-icon" />}
          </span>
        )}
        {loc.detail && <span className="dr-location-detail">{loc.detail}</span>}
        {loc.mapUrl && (
          <a
            href={loc.mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="dr-map-link"
          >
            Map
          </a>
        )}
      </div>
    );
  };

  // ── Edit Handlers ──
  const handleEditStart = (record) => {
    setEditingRow(`${record.empId}-${record.date}`);
    setNewStatus(record.status);
  };

  const handleEditCancel = () => {
    setEditingRow(null);
    setNewStatus("");
  };

  const handleEditSave = async (record) => {
    if (newStatus === record.status) {
      handleEditCancel();
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/attendances/admin-update`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          empId: record.empId,
          date: record.date,
          status: newStatus
        })
      });

      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await res.text();
        console.error(`Backend returned HTTP ${res.status} non-JSON:`, text.substring(0, 500));
        throw new Error(`Backend Error! Route /api/attendances/admin-update returned HTTP ${res.status}. Response: ${text.substring(0, 100)}...`);
      }
      const json = await res.json();
      if (!json.success && !res.ok) throw new Error(json.message || "Failed to update attendance status");

      // Update local state
      setAttendanceData(prev => prev.map(r =>
        (r.empId === record.empId && r.date === record.date) ? { ...r, status: newStatus } : r
      ));

      toast.success("Attendance status updated successfully!");
      handleEditCancel();
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Something went wrong while updating.");
    } finally {
      setSaving(false);
    }
  };

  // CSV Export logic
  const handleExportCSV = () => {
    if (filteredData.length === 0) return;

    const headers = (filterType === "last7" || filterType === "custom" || filterType === "tomorrow" || filterType === "yesterday")
      ? ["Date", "Employee ID", "Employee Name", "Check-in", "Check-out", "Location", "Status"]
      : ["Employee ID", "Employee Name", "Check-in", "Check-out", "Location", "Status"];

    const rows = filteredData.map(r => {
      const rowData = [
        r.empId,
        r.name,
        formatDisplayTime(r.checkIn),
        formatDisplayTime(r.checkOut),
        formatLocationForCSV(r.location),
        r.status === "P" ? "Present" : r.status === "A" ? "Absent" : r.status === "H" ? (r.holidayName ? `Holiday (${r.holidayName})` : "Holiday") : r.status === "WFH" ? "Working (WFH)" : r.status === "P/WFH" ? "Present (WFH)" : r.status
      ];
      if (filterType === "last7" || filterType === "custom" || filterType === "tomorrow" || filterType === "yesterday") {
        rowData.unshift(r.date);
      }
      return rowData;
    });

    const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `attendance_report_${filterType}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Print Logic
  const handlePrint = () => {
    window.print();
  };

  const handleReset = () => {
    setSearchQuery("");
    setFilterType("today");
    setCustomDate(new Date().toISOString().split('T')[0]);
    setStatusFilter("ALL");
    setDeptFilter("ALL");
  };

  return (
      <MainLayout sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} title="Daily Attendance Status" subtitle="View and manage daily attendance">

        <div className="dr-container">


          <section className="dr-filters-section">
            <div className="dr-filter-row" style={{ flexWrap: 'wrap', gap: '15px' }}>
              <div className="dr-search-box">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search by name or ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>STATUS:</span>
                <select 
                  value={statusFilter} 
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '12px', color: '#334155', outline: 'none', background: '#fff', cursor: 'pointer' }}
                >
                  <option value="ALL">All</option>
                  <option value="Present">Present</option>
                  <option value="Absent">Absent</option>
                  <option value="Late">Late</option>
                  <option value="Leave">Leave</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>DEPT:</span>
                <select 
                  value={deptFilter} 
                  onChange={(e) => setDeptFilter(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '12px', color: '#334155', outline: 'none', background: '#fff', cursor: 'pointer' }}
                >
                  <option value="ALL">All Departments</option>
                  {Array.from(new Set(attendanceData.map(d => d.department))).filter(Boolean).sort().map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>

              <div className="dr-filter-menu-wrapper">
                <button
                  className={`dr-filter-toggle-btn ${showFilters ? 'active' : ''}`}
                  onClick={toggleFilters}
                  title="Filter Attendance"
                >
                  <Filter size={18} />
                  <span>Filters</span>
                </button>

                {showFilters && (
                  <div className="dr-filter-dropdown">
                    <div className="dr-dropdown-header">
                      <span>Quick Filters</span>
                      <button onClick={() => setShowFilters(false)}><X size={14} /></button>
                    </div>
                    <div className="dr-quick-filters">
                      {['today', 'yesterday', 'tomorrow', 'last7'].map(type => (
                        <button
                          key={type}
                          className={`dr-tab-btn ${filterType === type ? 'active' : ''}`}
                          onClick={() => {
                            setFilterType(type);
                            if (type === 'tomorrow') {
                              const tom = new Date();
                              tom.setDate(tom.getDate() + 1);
                              setCustomDate(tom.toISOString().split('T')[0]);
                            }
                            setShowFilters(false); // Close after selection
                          }}
                        >
                          {type === "last7" ? "Last 7 Days" : type.charAt(0).toUpperCase() + type.slice(1)}
                        </button>
                      ))}
                    </div>
                    <div className="dr-custom-date-section">
                      <label>Custom Date:</label>
                      <div className="dr-custom-date">
                        <input
                          type="date"
                          value={customDate}
                          onChange={(e) => {
                            setCustomDate(e.target.value);
                            setFilterType("custom");
                            setShowFilters(false);
                          }}
                        />
                      </div>
                    </div>
                    <div style={{ padding: '12px 16px', borderTop: '1px solid #e2e8f0', marginTop: '12px' }}>
                      <button 
                        className="dr-reset-btn" 
                        onClick={() => {
                          handleReset();
                          setShowFilters(false);
                        }}
                        style={{ width: '100%', justifyContent: 'center', background: '#f8fafc' }}
                      >
                        <RotateCcw size={14} /> Reset Filters
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
                <Tooltip title="Export CSV" placement="top">
                  <button className="dr-action-btn" style={{ padding: '8px' }} onClick={handleExportCSV}>
                    <Download size={16} />
                  </button>
                </Tooltip>
                <Tooltip title="Print" placement="top">
                  <button className="dr-action-btn" style={{ padding: '8px' }} onClick={handlePrint}>
                    <Printer size={16} />
                  </button>
                </Tooltip>
              </div>
            </div>
          </section>

          <section className="dr-content printable-area">
            {loading ? (
              <div className="dr-loading">
                <Loader2 size={32} className="dr-spin" />
                <p>Loading attendance records...</p>
              </div>
            ) : error ? (
              <div className="dr-error">
                <p>Error: {error}</p>
                <button onClick={fetchReport}>Retry</button>
              </div>
            ) : (
              <div className="dr-table-wrapper">
                <table className="dr-table">
                  <thead>
                    <tr>
                      {(filterType === "last7" || filterType === "custom" || filterType === "tomorrow" || filterType === "yesterday") && <th>Date</th>}
                      <th>Employee ID</th>
                      <th>Employee Name</th>
                      <th>Department</th>
                      <th>Check-in Time</th>
                      <th>Check-out Time</th>
                      <th>Location</th>
                      <th>Status</th>
                      <th className="dr-action-col" style={{ width: '80px', textAlign: 'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.length === 0 ? (
                      <tr>
                        <td colSpan={(filterType === "today") ? "7" : "8"} className="dr-no-data">No records found for the selected criteria.</td>
                      </tr>
                    ) : (
                      filteredData.map((record, idx) => (
                        <tr key={`${record.empId}-${record.date}-${idx}`}>
                          {(filterType === "last7" || filterType === "custom" || filterType === "tomorrow" || filterType === "yesterday") && (
                            <td><span className="dr-date-label">{formatDateLabel(record.date)}</span></td>
                          )}
                          <td><span className="dr-id-badge">{record.empId}</span></td>
                          <td>
                            <div className="dr-emp-name">
                              {record.name}
                              {record.holidayName && (
                                <div style={{ fontSize: '0.75rem', color: '#8b5cf6', marginTop: '2px' }}>
                                  {record.holidayName}
                                </div>
                              )}
                            </div>
                          </td>
                          <td style={{ color: '#64748b' }}>{record.department}</td>
                          <td>{formatDisplayTime(record.checkIn)}</td>
                          <td>{formatDisplayTime(record.checkOut)}</td>
                          <td>{renderLocationCell(record.location)}</td>
                          <td>
                            {editingRow === `${record.empId}-${record.date}` ? (
                              <select
                                value={newStatus}
                                onChange={e => setNewStatus(e.target.value)}
                                className="dr-status-select"
                                disabled={saving}
                                style={{ padding: '4px', borderRadius: '4px', border: '1px solid #ccc' }}
                              >
                                <option value="P">Present</option>
                                <option value="H">Holiday</option>
                                <option value="CL">Casual Leave</option>
                                <option value="A">Absent</option>
                                <option value="SL">Sick Leave</option>
                                <option value="P/CL">Present/Casual Leave</option>
                                <option value="CL/P">Casual Leave/Present</option>
                                <option value="CO">Compenstry off</option>
                                <option value="WO">Week off</option>
                                <option value="SL/P">Sick Leave/Present</option>
                                <option value="P/SL">Present/Sick Leave</option>
                                <option value="-">-</option>
                              </select>
                            ) : (
                              <span className={`dr-status-badge ${record.status === "-" ? "dash" : record.status.toLowerCase().replace(/\//g, '-')}`}>
                                {record.status === "H" && record.holidayName
                                  ? `Holiday (${record.holidayName})`
                                  : {
                                      "P": "Present",
                                      "H": "Holiday",
                                      "CL": "Casual Leave",
                                      "A": "Absent",
                                      "SL": "Sick Leave",
                                      "P/CL": "Present/Casual Leave",
                                      "CL/P": "Casual Leave/Present",
                                      "CO": "Compenstry off",
                                      "WO": "Week off",
                                      "SL/P": "Sick Leave/Present",
                                      "P/SL": "Present/Sick Leave",
                                      "-": "-"
                                    }[record.status] || record.status}
                              </span>
                            )}
                          </td>
                          <td className="dr-action-col" style={{ textAlign: 'center' }}>
                            {editingRow === `${record.empId}-${record.date}` ? (
                              <div className="dr-edit-actions" style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                <button onClick={() => handleEditSave(record)} disabled={saving} title="Save" style={{ cursor: 'pointer', background: 'none', border: 'none', color: 'green' }}>
                                  <Check size={16} />
                                </button>
                                <button onClick={handleEditCancel} disabled={saving} title="Cancel" style={{ cursor: 'pointer', background: 'none', border: 'none', color: 'red' }}>
                                  <X size={16} />
                                </button>
                              </div>
                            ) : (
                              <button onClick={() => handleEditStart(record)} title="Edit Status" style={{ cursor: 'pointer', background: 'none', border: 'none', color: '#555' }}>
                                <Pencil size={16} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </MainLayout>
  );
}

